<script setup>
// SiteUp (M8): cartão de Pix copia-e-cola.
//
// O backend entrega `content_attributes.pix` pronto. Nada é parseado aqui
// — e a chave NUNCA vai para log/telemetria; só para o clipboard sob
// ação explícita.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from 'next/message/bubbles/Base.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useMessageContext } from '../provider.js';
import { toIntlLocale } from 'shared/helpers/intlLocale';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';

const { contentAttributes } = useMessageContext();
const { t, locale } = useI18n();
const copying = ref(false);

// O parse guarda o valor como veio. Formatar é só da exibição: se já tem
// símbolo/letra (ex. "R$ 25,12"), mantém; número cru vira moeda da locale.
function formatPixAmount(raw, currentLocale) {
  if (raw == null || raw === '') return '';
  const text = String(raw).trim();
  if (/[^\d\s.,\-']/.test(text)) return text;

  const normalized =
    text.includes(',') && text.includes('.')
      ? text.replace(/\./g, '').replace(',', '.')
      : text.replace(',', '.');
  const number = Number(normalized);
  if (!Number.isFinite(number)) return text;

  try {
    return new Intl.NumberFormat(toIntlLocale(currentLocale || 'pt-BR'), {
      style: 'currency',
      currency: 'BRL',
    }).format(number);
  } catch {
    return text;
  }
}

const pix = computed(() => contentAttributes.value?.pix ?? {});
const merchantName = computed(() => pix.value.merchantName || '');
const keyType = computed(() => pix.value.keyType || '');
const pixKey = computed(() => pix.value.key || '');
const referenceId = computed(() => pix.value.referenceId || '');

const displayAmount = computed(() =>
  formatPixAmount(pix.value.amount, locale.value)
);

const secondaryLine = computed(() => {
  return [merchantName.value, keyType.value].filter(Boolean).join(' · ');
});

const copyKey = async () => {
  if (!pixKey.value || copying.value) return;
  copying.value = true;
  try {
    await copyTextToClipboard(pixKey.value);
    useAlert(t('CONVERSATION.PIX.COPIED'));
  } catch {
    useAlert(t('CONVERSATION.PIX.COPY_FAILED'));
  } finally {
    copying.value = false;
  }
};
</script>

<template>
  <BaseBubble class="p-3">
    <div class="flex flex-col gap-2 min-w-[15rem]">
      <!-- Cores por HERANÇA + opacidade (regressão #438). -->
      <div class="flex items-center gap-1.5 opacity-80">
        <Icon icon="i-ph-qr-code" class="size-4" />
        <span class="text-xs font-medium uppercase tracking-wide">
          {{ t('CONVERSATION.PIX.TITLE') }}
        </span>
      </div>

      <p
        v-if="displayAmount"
        class="text-2xl font-semibold tabular-nums leading-tight"
      >
        {{ displayAmount }}
      </p>

      <p v-if="secondaryLine" class="text-xs opacity-70">
        {{ secondaryLine }}
      </p>

      <div
        v-if="pixKey"
        class="flex items-stretch gap-2 rounded-xl bg-current/10 overflow-hidden"
      >
        <code
          class="flex-1 min-w-0 px-3 py-2 text-xs font-mono break-all opacity-90"
        >
          {{ pixKey }}
        </code>
        <button
          type="button"
          class="shrink-0 min-h-11 min-w-11 px-3 text-xs font-medium opacity-90 hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px]"
          :disabled="copying"
          :aria-label="t('CONVERSATION.PIX.COPY')"
          @click="copyKey"
        >
          {{ t('CONVERSATION.PIX.COPY') }}
        </button>
      </div>

      <p v-if="referenceId" class="text-xs opacity-60">
        {{ t('CONVERSATION.PIX.REFERENCE', { id: referenceId }) }}
      </p>
    </div>
  </BaseBubble>
</template>
